import DeviationService from '../services/deviationService';
import { publishNotificationResolve } from '../infrastructure/events/legacyEventBridge';

const EMPTY = {
  selfDeviations: [],
  handoverDeviations: [],
  workerTolerances: {},
  pendingReviews: [],
  auditTrail: [],
  notifications: [],
  toleranceSystemEnabled: false
};

/** Same tolerance as DeviationService — ignore rounding noise as “écarts”. */
export const ECART_AMOUNT_TOLERANCE = 0.01;

/** Signed session écart (cash + electronic) when available. */
export function getEcartSignedAmount(deviation) {
  return DeviationService.signedDeviationAmount(deviation);
}

/** True only for real money differences outside tolerance. */
export function isMeaningfulEcart(deviation) {
  return Math.abs(getEcartSignedAmount(deviation)) > ECART_AMOUNT_TOLERANCE;
}

export function hasDiscrepancyManagementContent(doc) {
  if (!doc || typeof doc !== 'object') return false;
  return (
    (Array.isArray(doc.selfDeviations) && doc.selfDeviations.length > 0) ||
    (Array.isArray(doc.handoverDeviations) && doc.handoverDeviations.length > 0) ||
    (Array.isArray(doc.pendingReviews) && doc.pendingReviews.length > 0)
  );
}

function discrepancyRichnessScore(doc) {
  if (!doc || typeof doc !== 'object') return 0;
  return (
    (doc.selfDeviations?.length || 0) +
    (doc.handoverDeviations?.length || 0) +
    (doc.pendingReviews?.length || 0)
  );
}

export function normalizeDiscrepancyManagement(raw) {
  if (!raw || typeof raw !== 'object') return { ...EMPTY };
  return {
    ...EMPTY,
    ...raw,
    selfDeviations: Array.isArray(raw.selfDeviations) ? raw.selfDeviations : [],
    handoverDeviations: Array.isArray(raw.handoverDeviations) ? raw.handoverDeviations : [],
    pendingReviews: Array.isArray(raw.pendingReviews) ? raw.pendingReviews : []
  };
}

function deviationKey(deviation) {
  if (!deviation) return '';
  return String(
    deviation.id ||
    `${deviation.type || 'self'}_${deviation.workerId || deviation.previousWorkerId || ''}_${deviation.timestamp || ''}`
  );
}

/**
 * Fusionne plusieurs sources (React state, cache lecture, dashboard KPI) sans doublons.
 */
export function mergeDiscrepancySources(...sources) {
  const byId = new Map();

  for (const source of sources) {
    const normalized = normalizeDiscrepancyManagement(source);
    for (const deviation of [
      ...normalized.selfDeviations,
      ...normalized.handoverDeviations
    ]) {
      const key = deviationKey(deviation);
      if (!key) continue;
      byId.set(key, { ...byId.get(key), ...deviation });
    }
  }

  const merged = Array.from(byId.values());
  const richest = sources
    .filter((s) => s && typeof s === 'object')
    .sort((a, b) => discrepancyRichnessScore(b) - discrepancyRichnessScore(a))[0];

  return {
    ...EMPTY,
    ...(richest ? normalizeDiscrepancyManagement(richest) : {}),
    selfDeviations: merged.filter((d) => d.type !== 'handover'),
    handoverDeviations: merged.filter((d) => d.type === 'handover')
  };
}

function syntheticDeviationFromPendingReview(review) {
  if (!review?.deviationId) return null;
  const amount = Number(review.amount) || 0;
  if (Math.abs(amount) <= ECART_AMOUNT_TOLERANCE) return null;
  const isHandover = review.type === 'handover';
  return {
    id: review.deviationId,
    type: review.type || 'self',
    workerId: review.workerId,
    workerName: review.workerName,
    previousWorkerId: isHandover ? review.workerId : undefined,
    previousWorkerName: isHandover ? review.workerName : undefined,
    timestamp: review.timestamp || new Date().toISOString(),
    status: 'pending',
    deviation: {
      difference: amount,
      totalDifference: amount
    },
    explanation: ''
  };
}

function syntheticDeviationFromNotification(notification) {
  if (notification?.type !== 'deviation_created' || !notification.deviationId) return null;
  // Never resurrect dismissed / already-read ghosts into the manager modal
  if (notification.read) return null;
  const amount = Number(notification.amount) || 0;
  if (Math.abs(amount) <= ECART_AMOUNT_TOLERANCE) return null;
  const isHandover = notification.deviationType === 'handover';
  return {
    id: notification.deviationId,
    type: notification.deviationType || 'self',
    workerId: notification.workerId,
    workerName: notification.workerName,
    previousWorkerId: isHandover ? notification.workerId : undefined,
    previousWorkerName: isHandover ? notification.workerName : undefined,
    timestamp: notification.timestamp || new Date().toISOString(),
    status: 'pending',
    deviation: {
      difference: amount,
      totalDifference: amount
    },
    explanation: ''
  };
}

/**
 * Aperçu UI : inclut pendingReviews / notifications si selfDeviations vide (migration partielle).
 * Zero-amount / within-tolerance rows are never shown or counted as open écarts.
 */
export function expandDiscrepancyManagementForDisplay(raw) {
  const base = mergeDiscrepancySources(raw);
  const byId = new Map();
  for (const deviation of [...base.selfDeviations, ...base.handoverDeviations]) {
    if (!isMeaningfulEcart(deviation)) continue;
    const key = deviationKey(deviation);
    if (key) byId.set(String(deviation.id || key), deviation);
  }

  const normalized = normalizeDiscrepancyManagement(
    raw && typeof raw === 'object' ? raw : base
  );

  for (const review of normalized.pendingReviews) {
    const synthetic = syntheticDeviationFromPendingReview(review);
    if (synthetic && !byId.has(String(synthetic.id))) {
      byId.set(String(synthetic.id), synthetic);
    }
  }
  for (const notification of normalized.notifications) {
    const synthetic = syntheticDeviationFromNotification(notification);
    if (synthetic && !byId.has(String(synthetic.id))) {
      byId.set(String(synthetic.id), synthetic);
    }
  }

  const merged = Array.from(byId.values()).filter(isMeaningfulEcart);
  return {
    ...base,
    pendingReviews: normalized.pendingReviews,
    notifications: normalized.notifications,
    selfDeviations: merged.filter((d) => d.type !== 'handover'),
    handoverDeviations: merged.filter((d) => d.type === 'handover')
  };
}

export function countOpenEcarts(discrepancyManagement) {
  const expanded = expandDiscrepancyManagementForDisplay(discrepancyManagement);
  const all = [...expanded.selfDeviations, ...expanded.handoverDeviations];
  return all.filter((ecart) => isDeviationPending(ecart) && isMeaningfulEcart(ecart)).length;
}

export function isDeviationPending(deviation) {
  const status = deviation?.status || deviation?.managerReview?.decision || 'pending';
  return status === 'open' || status === 'pending';
}

function applyReviewToDeviation(deviation, decision, options = {}) {
  const { comments = '', managerId = 'manager' } = options;
  const reviewedAt = new Date().toISOString();
  return {
    ...deviation,
    status: decision,
    managerComments: comments,
    managerReview: {
      reviewedBy: managerId,
      reviewedAt,
      decision,
      comments
    }
  };
}

/**
 * Persiste une décision manager (accepter / rejeter) sur le document canonique.
 */
export function applyManagerDeviationDecision(doc, deviation, decision, options = {}) {
  if (!deviation?.id || !decision) {
    return normalizeDiscrepancyManagement(doc);
  }

  // DeviationService imported at module top
  const normalized = normalizeDiscrepancyManagement(doc);
  const deviationId = String(deviation.id);
  const listKey = deviation.type === 'handover' ? 'handoverDeviations' : 'selfDeviations';

  let found = false;
  const patchList = (list) =>
    (Array.isArray(list) ? list : []).map((entry) => {
      if (String(entry?.id) !== deviationId) return entry;
      found = true;
      return applyReviewToDeviation(entry, decision, options);
    });

  normalized.selfDeviations = patchList(normalized.selfDeviations);
  normalized.handoverDeviations = patchList(normalized.handoverDeviations);

  if (!found) {
    const reviewed = applyReviewToDeviation(deviation, decision, options);
    if (listKey === 'handoverDeviations') {
      normalized.handoverDeviations = [...normalized.handoverDeviations, reviewed];
    } else {
      normalized.selfDeviations = [...normalized.selfDeviations, reviewed];
    }
  }

  normalized.pendingReviews = (normalized.pendingReviews || []).filter(
    (review) => String(review?.deviationId || '') !== deviationId
  );

  normalized.notifications = (normalized.notifications || []).map((notification) =>
    String(notification?.deviationId || '') === deviationId
      ? { ...notification, read: true }
      : notification
  );

  // Clear matching inbox bell item (parallel to DM notification mark-read)
  try {
    publishNotificationResolve({
      deviationId,
      eventKey: `deviation_${deviationId}`
    });
  } catch {
    /* best-effort */
  }

  if (decision === 'rejected') {
    const workerId = deviation.workerId || deviation.previousWorkerId;
    const amount =
      Number(deviation?.deviation?.totalDifference ?? deviation?.deviation?.difference ?? 0) || 0;
    if (workerId) {
      normalized.workerTolerances = DeviationService.updateWorkerTolerance(
        workerId,
        DeviationService.getCurrentMonth(),
        normalized.workerTolerances || {},
        amount,
        decision
      );
    }
  }

  const auditEntry = DeviationService.createAuditEntry('manager_decision', {
    deviationId,
    decision,
    managerId: options.managerId || 'manager',
    workerId: deviation.workerId || deviation.previousWorkerId
  });
  normalized.auditTrail = [...(normalized.auditTrail || []), auditEntry];

  return normalized;
}
